import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export interface AIAgent {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  agent_type: 'data_validator' | 'anomaly_detector' | 'invoice_processor' | 'cost_optimizer' | 'report_generator' | 'collection_manager' | 'custom';
  status: 'active' | 'inactive' | 'paused' | 'error' | 'training';
  configuration: any;
  triggers: any;
  actions: any;
  model_config: any;
  performance_metrics: any;
  last_execution_at?: string;
  next_execution_at?: string;
  execution_count: number;
  success_rate: number;
  average_execution_time_ms?: number;
  is_scheduled: boolean;
  schedule_config?: any;
  created_at: string;
  updated_at: string;
}

export interface CreateAIAgentData {
  name: string;
  description?: string;
  agent_type: AIAgent['agent_type'];
  configuration?: any;
  triggers?: any;
  actions?: any;
  model_config?: any;
  is_scheduled?: boolean;
  schedule_config?: any;
}

export interface AgentExecution {
  id: string;
  agent_id: string;
  user_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  input_data?: any;
  output_data?: any;
  error_message?: string;
  execution_time_ms?: number;
  tokens_used?: number;
  cost_estimate?: number;
  triggered_by?: string;
  trigger_data?: any;
  started_at?: string;
  completed_at?: string;
  created_at: string;
}

export interface AgentTemplate {
  id: string;
  name: string;
  description?: string;
  agent_type: AIAgent['agent_type'];
  configuration: any;
  triggers: any;
  actions: any;
  model_config: any;
  is_public: boolean;
  created_by?: string;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface AgentAnalytics {
  total_agents: number;
  active_agents: number;
  total_executions: number;
  successful_executions: number;
  avg_success_rate: number;
  total_tokens_used: number;
  total_cost: number;
}

export const useAIAgents = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch all AI agents
  const {
    data: agents = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['ai-agents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_agents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as AIAgent[];
    },
  });

  // Fetch agent executions
  const useAgentExecutions = (agentId?: string) => {
    return useQuery({
      queryKey: ['agent-executions', agentId],
      queryFn: async () => {
        let query = supabase
          .from('ai_agent_executions')
          .select('*')
          .order('created_at', { ascending: false });

        if (agentId) {
          query = query.eq('agent_id', agentId);
        }

        const { data, error } = await query;
        if (error) throw error;
        return data as AgentExecution[];
      },
    });
  };

  // Fetch agent templates
  const useAgentTemplates = () => {
    return useQuery({
      queryKey: ['agent-templates'],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('ai_agent_templates')
          .select('*')
          .order('usage_count', { ascending: false });

        if (error) throw error;
        return data as AgentTemplate[];
      },
    });
  };

  // Fetch agent analytics
  const useAgentAnalytics = () => {
    return useQuery({
      queryKey: ['agent-analytics'],
      queryFn: async () => {
        const { data, error } = await supabase
          .rpc('get_agent_analytics');

        if (error) throw error;
        return data[0] as AgentAnalytics;
      },
    });
  };

  // Create agent mutation
  const createAgentMutation = useMutation({
    mutationFn: async (newAgent: CreateAIAgentData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const agentData = {
        ...newAgent,
        user_id: user.id,
        configuration: newAgent.configuration || {},
        triggers: newAgent.triggers || {},
        actions: newAgent.actions || {},
        model_config: newAgent.model_config || {},
      };

      const { data, error } = await supabase
        .from('ai_agents')
        .insert([agentData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      queryClient.invalidateQueries({ queryKey: ['agent-analytics'] });
      toast({
        title: "Agent created",
        description: "AI agent has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error creating agent",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update agent mutation
  const updateAgentMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<AIAgent> }) => {
      const { data, error } = await supabase
        .from('ai_agents')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      queryClient.invalidateQueries({ queryKey: ['agent-analytics'] });
      toast({
        title: "Agent updated",
        description: "AI agent has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating agent",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete agent mutation
  const deleteAgentMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('ai_agents')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      queryClient.invalidateQueries({ queryKey: ['agent-analytics'] });
      toast({
        title: "Agent deleted",
        description: "AI agent has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting agent",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Execute agent mutation
  const executeAgentMutation = useMutation({
    mutationFn: async ({ agentId, inputData }: { agentId: string; inputData?: any }) => {
      const { data, error } = await supabase.functions.invoke('execute-ai-agent', {
        body: { agentId, inputData }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-executions'] });
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      toast({
        title: "Agent executed",
        description: "AI agent execution has been started.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error executing agent",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Create agent from template mutation
  const createFromTemplateMutation = useMutation({
    mutationFn: async ({ templateId, name, description }: { templateId: string; name: string; description?: string }) => {
      const { data: template, error: templateError } = await supabase
        .from('ai_agent_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (templateError) throw templateError;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const agentData = {
        name,
        description: description || template.description,
        agent_type: template.agent_type,
        configuration: template.configuration,
        triggers: template.triggers,
        actions: template.actions,
        model_config: template.model_config,
        user_id: user.id,
      };

      const { data, error } = await supabase
        .from('ai_agents')
        .insert([agentData])
        .select()
        .single();

      if (error) throw error;

      // Update template usage count
      await supabase
        .from('ai_agent_templates')
        .update({ usage_count: template.usage_count + 1 })
        .eq('id', templateId);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agents'] });
      queryClient.invalidateQueries({ queryKey: ['agent-templates'] });
      toast({
        title: "Agent created",
        description: "AI agent has been created from template successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error creating agent from template",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    agents,
    isLoading,
    error,
    createAgent: createAgentMutation.mutate,
    updateAgent: updateAgentMutation.mutate,
    deleteAgent: deleteAgentMutation.mutate,
    executeAgent: executeAgentMutation.mutate,
    createFromTemplate: createFromTemplateMutation.mutate,
    isCreating: createAgentMutation.isPending,
    isUpdating: updateAgentMutation.isPending,
    isDeleting: deleteAgentMutation.isPending,
    isExecuting: executeAgentMutation.isPending,
    isCreatingFromTemplate: createFromTemplateMutation.isPending,
    useAgentExecutions,
    useAgentTemplates,
    useAgentAnalytics,
  };
};